import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Client } from '../types';
import {
  addAIKeyword, deleteAIKeyword, excludeCompetitor, getCompetitorRoster,
  listClients, restoreCompetitor, updateClient,
} from '../lib/api';
import type { CompetitorRoster } from '../types';
import { GBIBtn, GBICard, Icon, T, sharedStyles } from './Shared';
import { NewClientModal } from './NewClientModal';
import { useIsMobile } from '../lib/useIsMobile';

interface SettingsScreenProps {
  client: Client;
  aiKeywords: string[];
  readOnly?: boolean;
  // Called after add/delete so the parent reloads the bundle.
  onKeywordsChanged?: () => void | Promise<void>;
}

export function SettingsScreen({ client, aiKeywords, readOnly = false, onKeywordsChanged }: SettingsScreenProps) {
  const isMobile = useIsMobile();

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 14 : 28, background: T.bgVoid }}>
      <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 22, fontWeight: 700, color: T.fgPrimary, marginBottom: 24 }}>Settings</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 720 }}>
        <SectionHeader>Business profile</SectionHeader>
        <BusinessProfileEditor client={client} readOnly={readOnly} onSaved={onKeywordsChanged} />

        {!readOnly && <SectionHeader>Locations</SectionHeader>}
        {!readOnly && <LocationsManager client={client} />}

        <SectionHeader>AI Visibility keywords</SectionHeader>
        {!readOnly && (
          <AIKeywordsModeToggle
            clientId={client.id}
            mode={client.aiKeywordsMode ?? 'manual'}
            onChanged={onKeywordsChanged}
          />
        )}
        <AIKeywordsManager
          clientId={client.id}
          keywords={aiKeywords}
          readOnly={readOnly}
          onChanged={onKeywordsChanged}
        />

        {!readOnly && <SectionHeader>Competitors on the Dashboard</SectionHeader>}
        {!readOnly && <CompetitorManager clientId={client.id} />}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Competitors shown in the Dashboard's review comparison.
//
// The roster is chosen automatically — whoever occupies most of this client's
// own map grid, minus their own branches — and that is right nearly always.
// This is the override for when it is not: a same-name business that is not
// really a branch, a supplier that keeps surfacing, a listing that has closed.
// Excluding one drops it from every comparison straight away.

function CompetitorManager({ clientId }: { clientId: string }) {
  const [data, setData] = useState<CompetitorRoster | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    getCompetitorRoster(clientId)
      .then(d => { setData(d); setError(null); })
      .catch(e => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(reload, [clientId]);

  const act = async (fn: () => Promise<void>, placeId: string) => {
    setBusy(placeId);
    setError(null);
    try { await fn(); reload(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  if (error && !data) {
    return (
      <GBICard style={{ padding: '20px 24px' }}>
        <div style={{ fontSize: 13, color: T.magenta }}>{error}</div>
      </GBICard>
    );
  }
  if (!data) {
    return (
      <GBICard style={{ padding: '20px 24px' }}>
        <div style={{ fontSize: 13, color: T.fgMuted }}>Loading…</div>
      </GBICard>
    );
  }

  return (
    <GBICard style={{ padding: '20px 24px' }}>
      <div style={{ fontSize: 12.5, color: T.fgSecondary, lineHeight: 1.6, marginBottom: 16 }}>
        These are the businesses the client is compared against on their Dashboard. They are picked
        automatically from who shows up in the client's own map grid. Exclude one if it is not
        really a competitor — the roster refills from the next business down.
      </div>

      {data.roster.length === 0 && (
        <div style={{ fontSize: 13, color: T.fgMuted }}>
          No competitors yet — they appear after the first map scan.
        </div>
      )}

      {data.roster.map(c => (
        <div key={c.placeId} style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0',
          borderTop: `1px solid ${T.border}`,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: T.fgPrimary }}>{c.name}</div>
            <div style={{ fontSize: 11.5, color: T.fgMuted, marginTop: 2 }}>
              {c.totalReviews != null ? `${c.totalReviews.toLocaleString()} reviews` : 'no review data'}
            </div>
          </div>
          {data.canEdit && (
            <GBIBtn
              variant="secondary"
              size="sm"
              disabled={busy === c.placeId}
              onClick={() => act(() => excludeCompetitor(clientId, c.placeId, c.name), c.placeId)}
            >
              {busy === c.placeId ? 'Removing…' : 'Not a competitor'}
            </GBIBtn>
          )}
        </div>
      ))}

      {data.excluded.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: T.fgMuted, marginBottom: 8,
          }}>
            Excluded
          </div>
          {data.excluded.map(c => (
            <div key={c.placeId} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
              borderTop: `1px solid ${T.border}`,
            }}>
              <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: T.fgSecondary }}>
                {c.name ?? c.placeId}
              </div>
              {data.canEdit && (
                <GBIBtn
                  variant="ghost"
                  size="sm"
                  disabled={busy === c.placeId}
                  onClick={() => act(() => restoreCompetitor(clientId, c.placeId), c.placeId)}
                >
                  {busy === c.placeId ? 'Restoring…' : 'Put back'}
                </GBIBtn>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, fontSize: 12, color: T.magenta }}>{error}</div>
      )}
    </GBICard>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Business profile — editable name / location / category / GBP Place ID.
// These four fields are mirrored to the linked Client Intake record on save
// (and vice-versa), so editing here or in the Intake form never diverges.

const PROFILE_FIELDS = [
  { key: 'name',       label: 'Business Name', placeholder: 'e.g. Hotpot Signature', mono: false },
  { key: 'location',   label: 'Location',      placeholder: 'e.g. Johor Bahru',      mono: false },
  { key: 'category',   label: 'Category',      placeholder: 'e.g. Hotpot Shabu',     mono: false },
  { key: 'gbpPlaceId', label: 'GBP Place ID',  placeholder: 'ChIJ… (from Google Place ID Finder)', mono: true },
] as const;

type ProfileForm = Record<(typeof PROFILE_FIELDS)[number]['key'], string>;

function profileFromClient(client: Client): ProfileForm {
  return {
    name: client.name ?? '',
    location: client.location ?? '',
    category: client.category ?? '',
    gbpPlaceId: client.gbpPlaceId ?? '',
  };
}

const profileLabelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: T.fgMuted, marginBottom: 6,
};

function BusinessProfileEditor({ client, readOnly, onSaved }: {
  client: Client;
  readOnly: boolean;
  onSaved?: () => void | Promise<void>;
}) {
  const [form, setForm] = useState<ProfileForm>(() => profileFromClient(client));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Re-seed the form when the bundle reloads with fresh values — e.g. after an
  // edit on the Intake side propagated here, or after our own save.
  useEffect(() => {
    setForm(profileFromClient(client));
  }, [client.id, client.name, client.location, client.category, client.gbpPlaceId]);

  const base = profileFromClient(client);
  const dirty = PROFILE_FIELDS.some(f => form[f.key] !== base[f.key]);

  const set = (k: keyof ProfileForm, v: string) => { setForm(f => ({ ...f, [k]: v })); setMsg(null); };

  const save = async () => {
    if (!form.name.trim() || !form.location.trim()) {
      setMsg({ kind: 'err', text: 'Business name and location are required.' });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      await updateClient(client.id, {
        name: form.name.trim(),
        location: form.location.trim(),
        category: form.category.trim(),
        gbpPlaceId: form.gbpPlaceId.trim(),
      });
      await onSaved?.();
      setMsg({ kind: 'ok', text: '✓ Saved — synced to the Client Intake record.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  };

  // Read-only viewers (client-role login / public share link) keep the static
  // card look — no inputs, no save.
  if (readOnly) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {PROFILE_FIELDS.map(f => (
          <GBICard key={f.key} style={{ padding: '16px 20px' }}>
            <div style={profileLabelStyle}>{f.label}</div>
            <div style={{ fontFamily: sharedStyles.fontMono, fontSize: 14, color: T.fgPrimary }}>
              {form[f.key] || '—'}
            </div>
          </GBICard>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {PROFILE_FIELDS.map(f => (
        <GBICard key={f.key} style={{ padding: '16px 20px' }}>
          <div style={profileLabelStyle}>{f.label}</div>
          <input
            value={form[f.key]}
            onChange={e => set(f.key, e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && dirty && !saving) save(); }}
            placeholder={f.placeholder}
            style={{
              width: '100%', padding: '9px 12px',
              fontFamily: f.mono ? sharedStyles.fontMono : sharedStyles.fontBody,
              fontSize: f.mono ? 12 : 14, color: T.fgPrimary,
              background: '#fff', border: `1px solid ${T.border}`, borderRadius: 6,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          {f.key === 'gbpPlaceId' && (
            <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 6, lineHeight: 1.5 }}>
              Re-points all Google Places & Local Falcon data to this business on the next sync.
            </div>
          )}
        </GBICard>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <GBIBtn variant="primary" onClick={save} disabled={!dirty || saving}
          style={{ alignSelf: 'flex-start', opacity: dirty && !saving ? 1 : 0.6 }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </GBIBtn>
        {dirty && !saving && (
          <span style={{ fontSize: 11, color: '#C0790F', fontWeight: 600 }}>● Unsaved changes</span>
        )}
      </div>

      {msg && (
        <div style={{
          padding: '8px 12px', borderRadius: 6, fontSize: 12,
          background: msg.kind === 'ok' ? 'rgba(30,158,90,0.08)' : 'rgba(232,80,26,0.08)',
          border: `1px solid ${msg.kind === 'ok' ? '#1E9E5A' : '#E8501A'}`,
          color: msg.kind === 'ok' ? '#15703f' : '#C03B0F',
        }}>{msg.text}</div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Locations — list this business's branches + add a new one.

function LocationsManager({ client }: { client: Client }) {
  const navigate = useNavigate();
  const [all, setAll] = useState<Client[] | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const reload = () => listClients().then(setAll).catch(() => setAll([]));
  useEffect(() => { void reload(); }, []);

  const headId = client.parentClientId ?? client.id;
  const locations = (all ?? [])
    .filter(c => c.id === headId || c.parentClientId === headId)
    .sort((a, b) => (a.id === headId ? -1 : b.id === headId ? 1 : a.name.localeCompare(b.name)));
  const head = (all ?? []).find(c => c.id === headId) ?? client;

  return (
    <GBICard style={{ padding: '20px 24px' }}>
      <div style={{ fontSize: 12, color: T.fgSecondary, marginBottom: 14, lineHeight: 1.5 }}>
        Branches of <strong style={{ color: T.fgPrimary }}>{head.name}</strong>. Each branch
        keeps its own Google place_id, heatmap, AI Visibility and competitors. Switch
        between them from the location dropdown in the top bar.
      </div>

      {all === null ? (
        <div style={{ fontSize: 12, color: T.fgMuted }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {locations.map(l => {
            const isCurrent = l.id === client.id;
            const isHead = l.id === headId;
            return (
              <div key={l.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: 6,
                background: isCurrent ? 'rgba(18,181,196,0.07)' : T.bgSubtle,
                border: `1px solid ${isCurrent ? T.cyan : T.border}`,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.fgPrimary }}>
                    {l.locationLabel?.trim() || l.location}
                    {isHead && <span style={{ fontSize: 9, color: T.cyan, marginLeft: 6 }}>● primary</span>}
                    {isCurrent && <span style={{ fontSize: 9, color: T.fgMuted, marginLeft: 6 }}>(viewing)</span>}
                  </div>
                  <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 2, fontFamily: sharedStyles.fontMono }}>
                    📍 {l.location} · {l.gbpPlaceId ?? 'no place_id'}
                  </div>
                </div>
                {!isCurrent && (
                  <button
                    onClick={() => navigate(`/admin/clients/${l.id}`)}
                    style={{
                      background: 'transparent', border: `1px solid ${T.border}`,
                      borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
                      fontSize: 11, color: T.fgSecondary, fontWeight: 600,
                    }}
                  >Open</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <GBIBtn variant="secondary" onClick={() => setShowAdd(true)}>+ Add location</GBIBtn>

      {showAdd && (
        <NewClientModal
          defaultParent={{ id: headId, name: head.name }}
          onClose={() => setShowAdd(false)}
          onCreated={c => { setShowAdd(false); navigate(`/admin/clients/${c.id}`); }}
        />
      )}
    </GBICard>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// AI keywords sync mode toggle — see migration 0013 for the semantics.

function AIKeywordsModeToggle({
  clientId, mode, onChanged,
}: {
  clientId: string;
  mode: 'auto' | 'manual';
  onChanged?: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setMode = async (next: 'auto' | 'manual') => {
    if (busy || next === mode) return;
    setBusy(true);
    setErr(null);
    try {
      await updateClient(clientId, { aiKeywordsMode: next });
      await onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <GBICard style={{ padding: '16px 20px' }}>
      <div style={{ fontSize: 12, color: T.fgSecondary, marginBottom: 10, lineHeight: 1.5 }}>
        <strong style={{ color: T.fgPrimary }}>How AI keywords sync:</strong> Client Intake is the source of truth. The list below mirrors the intake's "Schedule top N" selection (or the separate AI-prompt list if you turned that on). Pick how GBI handles any extra keywords Local Falcon might still report.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <ModeOption
          active={mode === 'manual'}
          label="Manual (recommended)"
          desc="Track only the prompts in this list — exactly what was selected in Client Intake. Anything Local Falcon scans outside this list is ignored. Editing the intake's keywords or top-N count re-mirrors here automatically."
          onClick={() => setMode('manual')}
          disabled={busy}
        />
        <ModeOption
          active={mode === 'auto'}
          label="Auto (legacy)"
          desc="GBI auto-discovers any keyword that has AI scans in Local Falcon and adds it to the list below. Use only if you're driving prompts from LF directly instead of Client Intake."
          onClick={() => setMode('auto')}
          disabled={busy}
        />
      </div>
      {err && (
        <div style={{
          marginTop: 10, padding: '8px 12px',
          background: 'rgba(232,80,26,0.08)', border: '1px solid #E8501A',
          borderRadius: 6, fontSize: 12, color: '#C03B0F',
        }}>{err}</div>
      )}
    </GBICard>
  );
}

function ModeOption({ active, label, desc, onClick, disabled }: {
  active: boolean; label: string; desc: string;
  onClick: () => void; disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1, textAlign: 'left',
        padding: '12px 14px',
        background: active ? 'rgba(18,181,196,0.08)' : T.bgSubtle,
        border: `1px solid ${active ? T.cyan : T.border}`,
        borderRadius: 8, cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div style={{
        fontFamily: sharedStyles.fontDisplay, fontSize: 13, fontWeight: 700,
        color: active ? T.cyan : T.fgPrimary, marginBottom: 4,
      }}>
        {active ? '● ' : '○ '}{label}
      </div>
      <div style={{ fontSize: 11, color: T.fgMuted, lineHeight: 1.5 }}>{desc}</div>
    </button>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: sharedStyles.fontDisplay, fontSize: 16, fontWeight: 700,
      color: T.fgPrimary, paddingBottom: 4,
      borderBottom: `1px solid ${T.border}`,
    }}>{children}</div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// AI Keywords management

function AIKeywordsManager({
  clientId, keywords, readOnly, onChanged,
}: {
  clientId: string;
  keywords: string[];
  readOnly: boolean;
  onChanged?: () => void | Promise<void>;
}) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (keywords.some(k => k.toLowerCase() === trimmed.toLowerCase())) {
      setError('That prompt is already in the list.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await addAIKeyword(clientId, trimmed);
      setDraft('');
      await onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (keyword: string) => {
    if (!confirm(`Remove "${keyword}"? Its scan history will also be deleted.`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAIKeyword(clientId, keyword);
      await onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <GBICard style={{ padding: '20px 24px' }}>
      <div style={{ fontSize: 12, color: T.fgSecondary, marginBottom: 12, lineHeight: 1.5 }}>
        These prompts are tracked weekly across ChatGPT, Gemini, and Google AI Overview.
        Phrase them as natural questions a real user would ask an AI search engine
        (e.g. "best digital marketing agency in Johor Bahru").
      </div>
      <div style={{
        marginBottom: 16, padding: '10px 12px',
        background: 'rgba(18,181,196,0.08)', border: `1px solid ${T.cyan}`,
        borderRadius: 6, fontSize: 11, color: T.fgSecondary, lineHeight: 1.6,
      }}>
        <strong style={{ color: T.cyan }}>Sourced from Client Intake:</strong> this list
        mirrors the intake's effective AI-prompt set (top-N of the reordered keywords
        when "AI Prompt same as keyword" is on, else the separate AI-prompt list). Edit
        the intake to add/remove/reorder — the change applies here automatically. Manual
        edits below are allowed but get overwritten on the next intake edit.
      </div>
      <div style={{
        marginBottom: 16, padding: '10px 12px',
        background: 'rgba(245,160,32,0.08)', border: `1px solid ${T.amber}`,
        borderRadius: 6, fontSize: 11, color: T.fgSecondary, lineHeight: 1.6,
      }}>
        <strong style={{ color: T.amber }}>How tracking works:</strong> prompts here are
        scanned weekly on the 3 platforms (ChatGPT, Gemini, Google AI Overview). Results
        sync into this dashboard every Monday. Allow up to 7 days for a newly added prompt
        to appear with data — adding it here doesn't trigger an immediate scan.
      </div>

      {keywords.length === 0 ? (
        <div style={{ fontSize: 12, color: T.fgMuted, padding: '12px 0', fontStyle: 'italic' }}>
          No prompts yet. Add your first one below.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {keywords.map(k => (
            <div key={k} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px',
              background: T.bgSubtle, border: `1px solid ${T.border}`, borderRadius: 6,
            }}>
              <span style={{ fontFamily: sharedStyles.fontMono, fontSize: 12, color: T.fgPrimary }}>
                {k}
              </span>
              {!readOnly && (
                <button
                  onClick={() => handleDelete(k)}
                  disabled={busy}
                  style={{
                    background: 'transparent', border: 'none',
                    color: T.fgMuted, cursor: busy ? 'wait' : 'pointer',
                    fontSize: 11, padding: '4px 8px', borderRadius: 4,
                  }}
                  title="Remove this prompt"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={draft}
            onChange={e => { setDraft(e.target.value); setError(null); }}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            placeholder='e.g. "best digital marketing agency in Johor Bahru"'
            disabled={busy}
            style={{
              flex: 1, padding: '10px 14px', fontSize: 13,
              fontFamily: sharedStyles.fontMono, color: T.fgPrimary,
              background: '#fff', border: `1px solid ${T.border}`, borderRadius: 6,
              outline: 'none',
            }}
          />
          <GBIBtn variant="primary" onClick={handleAdd} disabled={busy || !draft.trim()}>
            <Icon name="zap" size={12} color={T.bgVoid} /> Add
          </GBIBtn>
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 12, padding: '8px 12px',
          background: 'rgba(232,80,26,0.08)', border: '1px solid #E8501A',
          borderRadius: 6, fontSize: 12, color: '#C03B0F',
        }}>
          {error}
        </div>
      )}
    </GBICard>
  );
}
